import React, { useState, useEffect } from "react";
import { Wallet, Check, Menu, X } from "lucide-react";
import { useWallet } from "../context/WalletContext";
import Logo from "./Logo";
import { toast } from "sonner";

const NAV = [
  { label: "Burn", href: "#top" },
  { label: "Launch a coin", href: "#launch" },
  { label: "Leaderboard", href: "#burns" },
  { label: "Follow", href: "https://x.com/zstamp_fun", external: true },
];

const X_URL = "https://x.com/zstamp_fun";
const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
  </svg>
);

export default function Header() {
  const { short, address, connecting, connect, disconnect } = useWallet();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleConnect = async () => {
    if (address) { disconnect(); toast("Wallet disconnected"); return; }
    const a = await connect();
    toast.success("Connected to BNB Chain", { description: `${a.slice(0, 6)}…${a.slice(-4)}` });
  };

  return (
    <header className="sticky top-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(13,14,15,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
      }}>
      <div className="container-x flex items-center justify-between h-16">
        <div className="flex items-center gap-7">
          <a href="#top" className="flex items-center gap-2.5 shrink-0">
            <Logo size={34} />
            <div className="leading-none">
              <div className="font-number font-bold text-[17px] tracking-tight">ZSTAMP</div>
              <div className="label" style={{ fontSize: 8.5, letterSpacing: "0.1em" }}>burn it, keep the receipt</div>
            </div>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <a key={n.label} href={n.href} target={n.external ? "_blank" : undefined} rel={n.external ? "noreferrer" : undefined}
                 className="nav-link link-underline transition-colors" style={{ color: "var(--text)" }}
                 onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
                 onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text)")}>
                {n.label}
              </a>
            ))}
          </nav>
        </div>
        <button className="lg:hidden h-9 w-9 grid place-items-center rounded" style={{ border: "1px solid var(--border)" }} onClick={() => setOpen(!open)}>
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>

        <div className="flex items-center gap-2">
          <a href={X_URL} target="_blank" rel="noreferrer" aria-label="Follow ZSTAMP on X"
             className="grid place-items-center h-9 w-9 rounded transition-colors"
             style={{ border: "1px solid var(--border-strong)", color: "var(--text)" }}
             onMouseEnter={(e) => { e.currentTarget.style.color = "var(--gold)"; e.currentTarget.style.borderColor = "var(--gold)"; }}
             onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text)"; e.currentTarget.style.borderColor = "var(--border-strong)"; }}>
            <XIcon size={15} />
          </a>
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 h-8 rounded text-[12px] label" style={{ color: "#F0B90B", letterSpacing: "0.06em" }}>
            <img src="https://flap.sh/bnb.svg" alt="BNB" className="h-3.5 w-3.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            BNB Chain
          </div>
          <button onClick={handleConnect} disabled={connecting}
            className="flex items-center gap-2 h-9 px-4 rounded nav-link transition-all disabled:opacity-60"
            style={{ background: address ? "transparent" : "var(--red)", color: address ? "var(--text)" : "#fff", border: address ? "1px solid var(--border-strong)" : "none", fontSize: 12 }}>
            {address ? <Check size={14} style={{ color: "var(--green)" }} /> : <Wallet size={14} />}
            {connecting ? "Connecting…" : address ? short : "Connect Wallet"}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden container-x pb-4 flex flex-col gap-2" style={{ background: "rgba(13,14,15,0.97)" }}>
          {NAV.map((n) => (
            <a key={n.label} href={n.href} target={n.external ? "_blank" : undefined} onClick={() => setOpen(false)} className="nav-link py-2" style={{ color: "var(--text)" }}>{n.label}</a>
          ))}
        </div>
      )}
    </header>
  );
}
